import * as UTIF from 'utif'
import * as pdfjsLib from 'pdfjs-dist'
import { post, get, put } from '../../../utils/api'

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`
const API_BASE_URL = 'http://localhost:8000'

export const mergeExplainabilityInfo = async (blueprintSchema, explainability_info) => {
  const updatedSchema = await JSON.parse(JSON.stringify(blueprintSchema)) // deep clone to avoid mutation
  for (const key in explainability_info) {
    const explainInfo = explainability_info[key]
    const targetField = updatedSchema?.properties[key]
    if (targetField) {
      targetField.isEditing = false
      targetField.value = explainInfo?.value
      targetField.confidence = explainInfo?.confidence

      if (Array.isArray(explainInfo?.geometry) && explainInfo.geometry.length > 0) {
        targetField.page = explainInfo?.geometry[0]?.page
        targetField.geometry = explainInfo?.geometry
      }
    }
  }

  return updatedSchema
}

export const pollForStatus = async (invocationArn, interval = 10000, maxAttempts = 30) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const res = await get(`/v1/bedrock/get-bda-status?invocationArn=${invocationArn}`)
    const data = res.data
    if (data.status === 'Success' || data.status === 'FAILED') {
      return data
    }
    await new Promise((resolve) => setTimeout(resolve, interval))
  }
  throw new Error('Max polling attempts reached.')
}

export const createDataStructure = async (data) => {
  const documentClass = data?.document_class?.type || ''
  const explainability = Array.isArray(data.explainability_info) && data.explainability_info[0] ? data.explainability_info[0] : {}

  const schema = {
    $schema: 'http://json-schema.org/draft-07/schema#',
    description: '',
    class: documentClass,
    type: 'object',
    definitions: {},
    properties: {},
  }

  for (const key in explainability) {
    const fieldInfo = explainability[key]
    schema.properties[key] = {
      type: fieldInfo.type || 'string',
      inferenceType: 'inferred',
      instruction: '',
      isEditing: false,
      value: fieldInfo.value ?? '',
      confidence: fieldInfo.confidence ?? null,
      page: Array.isArray(fieldInfo.geometry) && fieldInfo.geometry[0]?.page ? fieldInfo.geometry[0].page : null,
      geometry: fieldInfo.geometry || [],
    }
  }
  return schema
}

export const convertPdfToImageArray = async (file) => {
  const fileReader = new FileReader()
  return new Promise((resolve, reject) => {
    fileReader.onload = async (e) => {
      try {
        const typedArray = new Uint8Array(e.target.result)
        const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const pages = []

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i)
          const viewport = page.getViewport({ scale: 1.5 })
          canvas.width = viewport.width
          canvas.height = viewport.height

          await page.render({ canvasContext: ctx, viewport }).promise
          const imageDataUrl = canvas.toDataURL('image/png')

          pages.push({
            id: String(i),
            name: `Page ${i}`,
            imageUrl: imageDataUrl,
          })
        }

        resolve(pages)
      } catch (err) {
        reject(err)
      }
    }

    fileReader.onerror = (e) => reject(e)
    fileReader.readAsArrayBuffer(file)
  })
}
export const convertJpegToImageArray = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = function (event) {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0)

        const imageUrl = canvas.toDataURL('image/png') // or image/jpeg if preferred

        resolve([
          {
            id: '1',
            name: file.name,
            imageUrl,
          },
        ])
      }
      img.onerror = reject
      img.src = event.target.result
    }

    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
export const convertTiffToImageArray = async (file) => {
  const arrayBuffer = await file.arrayBuffer()
  const ifds = UTIF.decode(arrayBuffer)
  const pages = []

  for (let i = 0; i < ifds.length; i++) {
    UTIF.decodeImage(arrayBuffer, ifds[i])

    const rgba = UTIF.toRGBA8(ifds[i]) // returns Uint8Array of RGBA pixels
    const { width, height } = ifds[i]

    // Create canvas and put image data
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')

    const imageData = ctx.createImageData(width, height)
    imageData.data.set(rgba)
    ctx.putImageData(imageData, 0, 0)

    // Convert to DataURL
    const imageUrl = canvas.toDataURL('image/png')
    pages.push({
      id: String(i + 1),
      name: `Page ${i + 1}`,
      imageUrl,
    })
  }

  return pages
}

/**
 * Extracts only the letter/label parts from a document-type string.
 * e.g. "22_BA,94_1767435648224_W9, BOL, 94_1765524572506_Invoices, VR New"
 *   → "BA,W9,BOL,Invoices,VR New"
 */
export const newFormatString = (input) => {
  if (!input || typeof input !== 'string') return ''
  return input
    .split(',')
    .map((segment) => {
      const s = segment.trim()
      const parts = s.split('_')
      if (parts.length >= 3 && /^\d+$/.test(parts[1])) {
        return parts[parts.length - 1].replace(/[_-]/g, ' ')
      }
      if (parts.length === 2 && /^\d+$/.test(parts[0])) {
        return parts[1]
      }
      if (parts.length >= 2) {
        return parts[parts.length - 1].replace(/[_-]/g, ' ')
      }
      return s
    })
    .join(',')
}
